/**
 * How one session day is summarised for an admin, in the teacher portal's vocabulary.
 *
 * The database keeps its own status values (`not_arrived` / `checked_in` / `picked_up` /
 * `absent`), but a teacher checks a student *in* on arrival and *out* at pickup, and the
 * admin pickup board must not invent a second vocabulary for the same events — an admin
 * reading "Attended" next to a teacher saying "Checked out" has to work out whether those
 * are the same thing.
 *
 * Shared by the attendance strip on the pickup roster and the per-record drill-down, so
 * the square and the modal behind it can never disagree about what a day was.
 */

import type { AttendanceStatus } from '../types/attendance';

export type AttendanceSlotStatus =
  | 'checked_in'
  | 'checked_out'
  | 'absent'
  | 'not_scheduled'
  | 'no_record'
  | 'upcoming'
  | 'not_enrolled'
  | 'no_session';

/**
 * Labels in declaration order, which is also the order the legend renders: the two normal
 * outcomes first, then the exceptions, and last the two days nothing happened on — one
 * because it has not happened yet, one because the program was closed.
 */
export const SLOT_LABEL: Record<AttendanceSlotStatus, string> = {
  checked_in: 'Checked in',
  checked_out: 'Checked out',
  absent: 'Absent',
  not_scheduled: 'Not scheduled (Flex)',
  no_record: 'No record',
  upcoming: 'Upcoming',
  not_enrolled: 'Not enrolled that day',
  no_session: 'No session',
};

/**
 * The square itself: fill and outline for one status, sized by the caller.
 *
 * Solid green is a child on site now and pale green a day already closed out, so an admin
 * working dismissal can find who is still here without reading a word. Kept next to the
 * labels — the strip, its legend, and the drill-down all draw the same square, and a
 * swatch that disagreed with the one you clicked would read as a different day.
 */
export const SLOT_SWATCH: Record<AttendanceSlotStatus, string> = {
  checked_in: 'bg-green-600',
  checked_out: 'bg-green-100 ring-1 ring-inset ring-green-600',
  absent: 'bg-red-500',
  // Mid gray, not red: a Flex student who did not pick today is not a no-show, and
  // colouring them like one teaches staff to ignore red. Solid and darker than the two
  // pale grays below, so "chose not to come" cannot be mistaken for "nothing on file"
  // or "no session".
  not_scheduled: 'bg-gray-400',
  no_record: 'bg-gray-100 ring-1 ring-inset ring-gray-300',
  // An empty outline is a day still waiting to happen; a filled block is a day that never
  // will. Neither carries the ring the recorded statuses use, so the eye skips both.
  upcoming: 'bg-white ring-1 ring-inset ring-gray-200',
  // A future week only: the child's own term has not started yet, or has already ended.
  // Dashed, so it reads as "outside their dates" rather than as any kind of attendance.
  not_enrolled: 'bg-white border border-dashed border-gray-400',
  no_session: 'bg-gray-200',
};

/**
 * Which of two records for the same student and day wins.
 *
 * A student with two after-school enrollments can have a record under each. Higher wins,
 * so a definite outcome is never masked by an untouched `not_arrived` row.
 */
export const STATUS_PRECEDENCE: Record<AttendanceStatus, number> = {
  picked_up: 3,
  checked_in: 2,
  absent: 1,
  not_arrived: 0,
};

/** The more meaningful of two statuses for the same student and day. */
export function bestOf(a: AttendanceStatus, b: AttendanceStatus): AttendanceStatus {
  return STATUS_PRECEDENCE[a] >= STATUS_PRECEDENCE[b] ? a : b;
}

export function toSlotStatus(status: AttendanceStatus): AttendanceSlotStatus {
  if (status === 'picked_up') return 'checked_out';
  if (status === 'checked_in') return 'checked_in';
  if (status === 'absent') return 'absent';
  // `not_arrived` is the seeded default. On a past day it means nobody recorded anything,
  // which is an unknown, not an absence — calling it absent would invent attendance data.
  return 'no_record';
}

/** First of the month for a YYYY-MM-DD day, as `flex_month_plans.plan_month` stores it. */
export function monthStart(date: string): string {
  return `${date.slice(0, 7)}-01`;
}

/**
 * What one day was for one student.
 *
 * A Flex student's unselected day is `not_scheduled` rather than an absence: they were
 * never expected, and colouring them like a no-show teaches staff to ignore the colour
 * that means a child is missing. A month with no plan row on file contributes nothing —
 * "no plan yet" is not the same as "chose no days", so those days fall through to
 * whatever attendance says.
 *
 * The last two rules exist because the roster shows a whole calendar week rather than only
 * days that have already run: a closure and a Thursday that has not arrived yet are both
 * empty squares, and neither may read as "nobody recorded anything".
 */
export function deriveSlotStatus(args: {
  date: string;
  isFlex: boolean;
  /** Months (as `YYYY-MM-01`) this student has a flex plan on file for. */
  flexMonths?: ReadonlySet<string>;
  /** Individual days this student's flex plans selected. */
  flexDays?: ReadonlySet<string>;
  /** The winning attendance status for the day, if any record exists. */
  recordStatus?: AttendanceStatus;
  /** False when the program did not meet that day — a closure or a weekday off schedule. */
  isSessionDay?: boolean;
  /** Business-TZ today. Omit for callers that only ever ask about days already past. */
  today?: string;
}): AttendanceSlotStatus {
  const { date, isFlex, flexMonths, flexDays, recordStatus, isSessionDay = true, today } = args;
  if (!isSessionDay) return 'no_session';
  if (isFlex && flexMonths?.has(monthStart(date)) && !flexDays?.has(date)) {
    return 'not_scheduled';
  }
  // An outcome outranks the calendar even on a day still ahead: an absence reported in
  // advance is real. `not_arrived` is not an outcome — it is the seeded default, and on a
  // future day it must not mask that the day has not happened.
  const recorded = recordStatus ? toSlotStatus(recordStatus) : 'no_record';
  if (recorded !== 'no_record') return recorded;
  return today !== undefined && date > today ? 'upcoming' : 'no_record';
}
